import React, { useState } from 'react';
import { Card, Modal, Space } from 'antd';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { LockRounded, LockOpenRounded, TrendingUpRounded, TrendingDownRounded, BalanceRounded,
    FitnessCenter,
    AddCircleRounded, CreateNewFolderRounded, ScaleRounded, BlenderRounded, ExpandMoreRounded } from '@mui/icons-material';
import TickerSearch from '../../../components/TickerSearch/TickerSearch';
import PortfolioSelector from '../../../pages/Portfolio/PortfolioSelectors/PortfolioSelector';
import ScaleEntry from './ScaleEntry';

const getAllowedTypes = (builderType) => {
    if (builderType === 'mm') {
        return ['b', 's', 'm'];
    }
    if (builderType === 'm') {
        return ['b', 's'];
    }
    if (builderType === 's') {
        return ['b'];
    }
    return [];
};

const BuilderToolbar = ({
    portfolioType,
    onEqualWeight,
    onLockAll,
    onUnlockAll,
    onProRataFill,
    onScaleUp,
    onScaleDown,
    onTickerSearch,
    onPortfolioSelection,
    onFetchBlendedTimeseries,
}) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalContent, setModalContent] = useState(null);
    const [currentValue, setCurrentValue] = useState(1);
    const [modalTitle, setModalTitle] = useState('');
    const [scaleAction, setScaleAction] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuType, setMenuType] = useState(null);

    const showModalForScale = (action) => {
        setModalTitle('Proportional Scaler');
        setIsModalVisible(true);
        setModalContent('scale');
        setScaleAction(() => (value) => action(value));
    };

    const showModalForAction = (actionType) => {
        setModalTitle(actionType === 'tickerSearch' ? 'Search for Tickers' : 'Search your Portfolios');
        setIsModalVisible(true);
        setModalContent(actionType);
    };

    const handleOk = (value) => {
        if (modalContent === 'scale' && scaleAction) {
            scaleAction(value || currentValue);
        }
        setIsModalVisible(false);
        setCurrentValue(1);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };

    const handleMenuOpen = (event, type) => {
        setAnchorEl(event.currentTarget);
        setMenuType(type);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuType(null);
    };

    const renderModalContent = () => {
        switch (modalContent) {
            case 'tickerSearch':
                return <TickerSearch onTickersSubmit={onTickerSearch} />;
            case 'portfolioSelection':
                return (
                    <PortfolioSelector
                        onSelect={onPortfolioSelection}
                        allowTypes={getAllowedTypes(portfolioType)}
                        maxSelected={10}
                        bodyWidth={500}
                    />
                );
            case 'scale':
                return <ScaleEntry currentValue={currentValue} onSubmit={handleOk} />;
            default:
                return null;
        }
    };

    return (
        <>
            <Space>
                <Button
                    variant={'contained'}
                    color={'primary'}
                    startIcon={<AddCircleRounded />}
                    onClick={() => showModalForAction('tickerSearch')}
                >
                    Add Ticker
                </Button>
                {portfolioType !== 'b' && (
                    <Button
                        variant={'contained'}
                        color={'primary'}
                        startIcon={<CreateNewFolderRounded />}
                        onClick={() => showModalForAction('portfolioSelection')}
                    >
                        Add Sleeve
                    </Button>
                )}

                {/* Weight Options Dropdown */}
                <Button
                    onMouseEnter={(e) => handleMenuOpen(e, 'weightOptions')}
                    startIcon={<FitnessCenter />}
                    endIcon={<ExpandMoreRounded />}
                >
                    Weight Schemes
                </Button>
                <Menu
                    anchorEl={anchorEl}
                    open={menuType === 'weightOptions'}
                    onClose={handleMenuClose}
                    MenuListProps={{ onMouseLeave: handleMenuClose }}
                >
                    <MenuItem onClick={() => { handleMenuClose(); onEqualWeight(); }}>Equal Weights</MenuItem>
                    <MenuItem onClick={() => { handleMenuClose(); onProRataFill(); }}>Pro-Rata to 100%</MenuItem>
                </Menu>
                <Button
                    onMouseEnter={(e) => handleMenuOpen(e, 'lockOptions')}
                    startIcon={<LockRounded />}
                    endIcon={<ExpandMoreRounded />}
                >
                    Lock / Unlock
                </Button>

                <Menu
                    anchorEl={anchorEl}
                    open={menuType === 'lockOptions'}
                    onClose={handleMenuClose}
                    MenuListProps={{ onMouseLeave: handleMenuClose }}
                >
                    <MenuItem onClick={() => { handleMenuClose(); onLockAll(); }}>Lock All</MenuItem>
                    <MenuItem onClick={() => { handleMenuClose(); onUnlockAll(); }}>Unlock All</MenuItem>
                </Menu>

                {/* Scale Options Dropdown */}
                <Button
                    onMouseEnter={(e) => handleMenuOpen(e, 'scaleOptions')}
                    startIcon={<ScaleRounded />}
                    endIcon={<ExpandMoreRounded />}
                >
                    Scaling Options
                </Button>
                <Menu
                    anchorEl={anchorEl}
                    open={menuType === 'scaleOptions'}
                    onClose={handleMenuClose}
                    MenuListProps={{ onMouseLeave: handleMenuClose }}
                >
                    <MenuItem onClick={() => { handleMenuClose(); showModalForScale(onScaleUp); }}>Scale Up</MenuItem>
                    <MenuItem onClick={() => { handleMenuClose(); showModalForScale(onScaleDown); }}>Scale Down</MenuItem>
                </Menu>
                <Button
                    onMouseEnter={(e) => handleMenuOpen(e, 'timeseriesOptions')}
                    startIcon={<TrendingUpRounded />}
                    endIcon={<ExpandMoreRounded />}
                >
                    Timeseries Options
                </Button>
                <Menu
                    anchorEl={anchorEl}
                    open={menuType === 'timeseriesOptions'}
                    onClose={handleMenuClose}
                    MenuListProps={{ onMouseLeave: handleMenuClose }}
                >
                    <MenuItem onClick={() => { handleMenuClose(); onFetchBlendedTimeseries(); }}>Blend Pro Rata</MenuItem>
                </Menu>

            </Space>

            <Modal
                width={960}
                title={modalTitle}
                open={isModalVisible}
                onOk={handleOk}
                onCancel={handleCancel}
                footer={null}
            >
                <Card bordered={false}>{renderModalContent()}</Card>
            </Modal>
        </>
    );
};

export default BuilderToolbar;
